import React, { useEffect, useRef, useState } from "react";
import { IconButton, Chip, Drawer, useMediaQuery, useTheme } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import CategoryIcon from "@mui/icons-material/Category";
import DescriptionIcon from "@mui/icons-material/Description";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Link, useParams } from "react-router-dom";
import "./ViewSupport.css";
import {
  fetchQueryStatusApi,
  isAdminReplyApi,
  sendChatApi,
  fetchQueryDetailsApi,
} from "../../services/supportManagement";
import { toastMessage } from "../../utils/toastMessage";
import ModalBox from "../../components/modal/ModalBox";
import constants from "../../utils/constants";
import { initiateSocketConnection } from "../../utils/common";
import { dateFormatWithTime } from "../../utils/dateFormat";
import AudioPlayer from "../../components/chatBox/AudioPlayer";

const _CURRENT_USER = {
  _id: '000000000000000000000000',
  name: 'Vedicmeet Admin',
  profileImage: 'https://vedicmeet.com/statics/vedic-meet_title-logo.webp',
};

const groupMessagesByDate = (msgs) => {
  const groups = {};
  msgs.forEach((msg) => {
    const d = msg.createdAt ? new Date(msg.createdAt) : new Date();
    const key = d.toDateString();
    if (!groups[key]) groups[key] = [];
    groups[key].push(msg);
  });
  return groups;
};

const formatDateLabel = (dateStr) => {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const MessageRenderer = ({ msg }) => {
  if (msg.type === 'html' && msg.metaEditor?.html) {
    return (
      <div>
        <span className="msg-type-badge">HTML</span>
        <div className="message-html" dangerouslySetInnerHTML={{ __html: msg.metaEditor.html }} />
      </div>
    );
  }
  if (msg.type === 'image' && msg.metadata?.fileUrl) {
    return (
      <div>
        <span className="msg-type-badge">Image</span>
        <img
          src={msg.metadata.fileUrl}
          alt="Shared"
          className="message-image"
          onClick={() => window.open(msg.metadata.fileUrl, '_blank')}
        />
      </div>
    );
  }
  if (msg.type === 'audio' && msg.metadata?.fileUrl) {
    return (
      <div>
        <span className="msg-type-badge">Audio</span>
        <AudioPlayer source={msg.metadata.fileUrl} />
      </div>
    );
  }
  return <div className="message-text">{msg.content}</div>;
};

// ── Extracted outside to prevent remount on parent re-render ──────────────
const DatewisePanel = ({ messages, userName, selectedDate, onSelectDate }) => {
  const sorted = [...messages].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  const grouped = groupMessagesByDate(sorted);
  const entries = Object.entries(grouped).sort(([a], [b]) => new Date(a) - new Date(b));
  return (
    <div className="datewise-panel">
      <div className="datewise-panel-header">
        <AccessTimeIcon style={{ fontSize: 15 }} />
        <span>Message Timeline</span>
      </div>
      {entries.length === 0 ? (
        <div className="datewise-empty">
          <ChatBubbleOutlineIcon style={{ fontSize: 36, color: '#d1d5db' }} />
          <p>No messages yet</p>
        </div>
      ) : (
        <div className="datewise-list">
          {selectedDate && (
            <button className="date-show-all-btn" onClick={() => onSelectDate(null)}>
              ✕ Show All Messages
            </button>
          )}
          {entries.map(([dateStr, msgs]) => {
            const last = msgs[msgs.length - 1];
            const isAdm = last?.from?.id === _CURRENT_USER._id;
            const preview = isMessageDeleted(last)
              ? 'This message was deleted'
              : (
                last?.content ||
                (last?.metaEditor?.html ? 'HTML message' : last?.metadata?.fileUrl ? 'Media attachment' : '—')
              );
            const isActive = selectedDate === dateStr;
            return (
              <div
                key={dateStr}
                className={`date-entry ${isActive ? 'date-entry-active' : ''}`}
                onClick={() => onSelectDate(isActive ? null : dateStr)}
              >
                <div className="date-entry-top">
                  <span className="date-entry-label">{formatDateLabel(dateStr)}</span>
                  <span className="date-entry-count">{msgs.length} msg{msgs.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="date-entry-preview">
                  <span className={`preview-sender ${isAdm ? 'sender-admin' : 'sender-user'}`}>
                    {isAdm ? 'Admin' : userName}:
                  </span>{' '}
                  {String(preview).substring(0, 55)}{String(preview).length > 55 ? '…' : ''}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const isMessageDeleted = (msg) => msg?.isDeleted?.forAll === true || msg?.isDeleted === true;

const ChatArea = ({ messages, messagesEndRef, selectedDate, onEdit, onDelete }) => {
  const filtered = selectedDate
    ? messages.filter(m => m.createdAt && new Date(m.createdAt).toDateString() === selectedDate)
    : messages;
  const sorted = [...filtered].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  const grouped = groupMessagesByDate(sorted);
  return (
    <div className="chat-messages">
      {selectedDate && (
        <div className="chat-filter-banner">
          Showing messages for <strong>{formatDateLabel(selectedDate)}</strong>
        </div>
      )}
      {Object.keys(grouped).length === 0 ? (
        <div className="chat-empty">
          <ChatBubbleOutlineIcon style={{ fontSize: 48, color: '#ccc' }} />
          <p>{selectedDate ? 'No messages on this date.' : 'No messages yet. Start the conversation.'}</p>
        </div>
      ) : (
        Object.entries(grouped)
          .sort(([a], [b]) => new Date(a) - new Date(b))
          .map(([dateStr, msgs]) => (
            <div key={dateStr}>
              <div className="chat-date-divider">
                <span>{formatDateLabel(dateStr)}</span>
              </div>
              {msgs.map((msg, idx) => {
                const isCurrent = msg?.from?.id === _CURRENT_USER._id;
                const deleted = isMessageDeleted(msg);
                const canEdit = !deleted && (msg.type === 'message' || !msg.type) && Boolean(msg.content);
                return (
                  <div key={msg._id || idx} className={`msg-row ${isCurrent ? 'msg-row-right' : 'msg-row-left'}`}>
                    <div className={`message-bubble ${isCurrent ? 'message-bubble-admin' : 'message-bubble-user'} ${deleted ? 'message-bubble-deleted' : ''}`}>
                      {deleted ? (
                        <div className="message-text message-deleted">This message was deleted</div>
                      ) : (
                        <MessageRenderer msg={msg} />
                      )}
                      <div className="msg-meta">
                        <span className="message-time">
                          {msg?.createdAt ? dateFormatWithTime(msg.createdAt) : ''}
                          {msg?.isEdited && !deleted ? ' · edited' : ''}
                        </span>
                        <span className={`message-sender-name ${isCurrent ? 'sender-admin' : 'sender-user'}`}>
                          {msg?.from?.name || '—'}
                        </span>
                      </div>
                      {!deleted && msg?._id && (
                        <div className="msg-actions">
                          {canEdit && (
                            <button type="button" className="msg-action-btn" onClick={() => onEdit(msg)}>
                              Edit
                            </button>
                          )}
                          <button type="button" className="msg-action-btn msg-action-delete" onClick={() => onDelete(msg)}>
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))
      )}
      <div ref={messagesEndRef} />
    </div>
  );
};

const MobileDrawerContent = ({ onClose, initials, userName, phone, phonePrefix, email, queryDetails }) => (
  <div className="mobile-drawer-panel">
    <div className="mobile-drawer-header">
      <span>User Details</span>
      <IconButton onClick={onClose} size="small">
        <CloseIcon fontSize="small" />
      </IconButton>
    </div>
    <div className="mobile-drawer-avatar">
      <div className="drawer-av-circle">{initials}</div>
      <div className="drawer-av-name">{userName}</div>
    </div>
    {phone && (
      <div className="drawer-info-row">
        <PhoneIcon style={{ fontSize: 16, color: '#9ca3af' }} />
        <div>
          <div className="drawer-info-label">Contact</div>
          <div className="drawer-info-value">{phonePrefix}{phone}</div>
        </div>
      </div>
    )}
    {email && (
      <div className="drawer-info-row">
        <EmailIcon style={{ fontSize: 16, color: '#9ca3af' }} />
        <div>
          <div className="drawer-info-label">Email</div>
          <div className="drawer-info-value">{email}</div>
        </div>
      </div>
    )}
    {queryDetails?.supportType && (
      <div className="drawer-info-row">
        <CategoryIcon style={{ fontSize: 16, color: '#9ca3af' }} />
        <div>
          <div className="drawer-info-label">Problem Type</div>
          <div className="drawer-info-value">{queryDetails.supportType}</div>
        </div>
      </div>
    )}
    {queryDetails?.description && (
      <div className="drawer-info-row">
        <DescriptionIcon style={{ fontSize: 16, color: '#9ca3af' }} />
        <div>
          <div className="drawer-info-label">Description</div>
          <div className="drawer-info-value">{queryDetails.description}</div>
        </div>
      </div>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
const ViewSupport = () => {
  const { status, id, name, threadId, isResolved, isAdminReply } = useParams();
  const isTicketResolved = Boolean(isResolved === 'true');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [updatedQueryStatusModal, setUpdateQueryStatusModal] = useState(false);
  const [queryDetails, setQueryDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusAction, setStatusAction] = useState('');
  const [uploading, setUploading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const _socket = useRef(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const userName = queryDetails?.userDetails?.name || name || 'User';
  const initials = userName.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  const phone = queryDetails?.userDetails?.details?.phone;
  const email = queryDetails?.userDetails?.details?.email;
  const phonePrefix =
    queryDetails?.userDetails?.details?.countryCode ||
    queryDetails?.userDetails?.details?.phonePrefix ||
    '';

  const fetchQueryDetails = async () => {
    try {
      const { data } = await fetchQueryDetailsApi(id);
      if (data?.code === 200) setQueryDetails(data?.result);
    } catch (error) {
      console.log('Error fetching query details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQueryStatus = async () => {
    try {
      const payload = { status: statusAction };
      if (statusAction === 'completed') payload.closedAt = new Date().toISOString();
      const { data } = await fetchQueryStatusApi(id, payload);
      toastMessage(data?.message, data?.code === 200 ? 'success' : 'error', 'queryUpdated');
      setUpdateQueryStatusModal(false);
      if (data?.code === 200) fetchQueryDetails();
    } catch (error) {
      console.log('error', error);
    }
  };

  const handleStatusAction = (action) => {
    setStatusAction(action);
    setUpdateQueryStatusModal(true);
  };

  const detectMessageType = (content) => {
    if (content.startsWith('<') && content.endsWith('>')) return 'html';
    if (content.match(/\.(jpg|jpeg|png|gif|webp)$/i)) return 'image';
    if (content.match(/\.(mp3|wav|ogg|m4a)$/i)) return 'audio';
    return 'message';
  };

  const createMessageObject = (content, type = null) => {
    const messageType = type || detectMessageType(content);
    const base = {
      threadId,
      from: { id: _CURRENT_USER._id, name: _CURRENT_USER.name, profileImg: _CURRENT_USER.profileImage },
      type: messageType,
      createdAt: new Date().toISOString(),
    };
    if (messageType === 'html') return { ...base, content: '', metaEditor: { html: content } };
    if (messageType === 'image') return { ...base, content: '', metadata: { url: content } };
    if (messageType === 'audio') return { ...base, content: '', metadata: { url: content } };
    return { ...base, content };
  };

  const applyMessageUpdate = (messageId, updater) => {
    setMessages((prev) => prev.map((item) => (item._id === messageId ? updater(item) : item)));
  };

  const emitMessageAction = (payload) => new Promise((resolve) => {
    if (!_socket.current?.connected) {
      toastMessage('Chat not connected yet, please wait…', 'error');
      resolve({ success: false });
      return;
    }
    _socket.current.emit('message-action', payload, (response) => {
      resolve(response || { success: false });
    });
  });

  const handleEditMessage = (msg) => {
    setEditingMessage(msg);
    setMessage(msg.content || '');
  };

  const cancelEdit = () => {
    setEditingMessage(null);
    setMessage('');
  };

  const confirmDeleteMessage = async () => {
    const msg = pendingDelete;
    setPendingDelete(null);
    if (!msg?._id) return;
    const response = await emitMessageAction({
      messageId: msg._id,
      threadId,
      action: 'delete',
      actionValues: { forAll: true },
    });
    if (!response?.success) {
      toastMessage(response?.error || response?.message || 'Failed to delete message', 'error');
      return;
    }
    const deletedState = response?.data?.isDeleted || { ...(msg.isDeleted || {}), forAll: true };
    applyMessageUpdate(msg._id, (item) => ({
      ...item,
      ...(response.data || {}),
      isDeleted: deletedState,
      content: 'Message Deleted',
    }));
    if (editingMessage?._id === msg._id) cancelEdit();
    toastMessage('Message hidden from the ticket', 'success');
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    if (!threadId || !isConnected || !_socket.current) {
      toastMessage('Chat not connected yet, please wait…', 'error');
      return;
    }
    if (editingMessage?._id) {
      const trimmed = message.trim();
      const response = await emitMessageAction({
        messageId: editingMessage._id,
        threadId,
        action: 'edit',
        actionValues: { content: trimmed },
      });
      if (!response?.success) {
        toastMessage(response?.error || response?.message || 'Failed to edit message', 'error');
        return;
      }
      applyMessageUpdate(editingMessage._id, (item) => ({
        ...item,
        ...(response.data || {}),
        content: trimmed,
        isEdited: true,
      }));
      cancelEdit();
      toastMessage('Message updated', 'success');
      return;
    }
    if (messages.length === 0 && isAdminReply === 'false') isAdminReplyApi(id);

    const newMessage = createMessageObject(message.trim());
    _socket.current.emit('send-message', { message: newMessage }, ({ newMessage: nm }) => {
      setMessages((prev) => [...prev, nm]);
      setMessage('');
    });

    try {
      const formData = new FormData();
      formData.append('customerSupportQueryId', id);
      let messageType = 0;
      if (newMessage.type === 'image') messageType = 1;
      else if (newMessage.type === 'audio') messageType = 2;
      else if (newMessage.type === 'html') messageType = 3;
      formData.append('messageType', messageType);
      formData.append('message', message);
      const { data } = await sendChatApi(formData);
      if (data.code !== 200) toastMessage(data.message, 'error', 'chatSend');
    } catch (err) {
      console.log('sendChatApi error:', err);
    }
    setTimeout(scrollToBottom, 200);
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fileUrl = URL.createObjectURL(file);
      const messageType = file.type.startsWith('image/') ? 'image' : 'audio';
      const newMessage = createMessageObject(fileUrl, messageType);
      _socket.current.emit('send-message', { message: newMessage }, ({ newMessage: nm }) => {
        setMessages((prev) => [...prev, nm]);
      });
      const formData = new FormData();
      formData.append('customerSupportQueryId', id);
      formData.append('messageType', messageType === 'image' ? 1 : 2);
      formData.append('file', file);
      const { data } = await sendChatApi(formData);
      if (data.code !== 200) toastMessage(data.message, 'error', 'fileUpload');
    } catch (error) {
      toastMessage('Failed to upload file', 'error');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    const socket = initiateSocketConnection({
      uri: `${constants.CHAT_SOCKET_SERVER}/chats`,
      currentUser: _CURRENT_USER,
      onConnect: () => setIsConnected(true),
    });
    if (socket) {
      _socket.current = socket;
      socket.on('new-message', (nm) => {
        setMessages((prev) => {
          if (prev.some(m => m._id && m._id === nm._id)) return prev;
          return [...prev, nm];
        });
        scrollToBottom();
      });
      socket.on('message-actions', ({ type, messageId, data }) => {
        if (!messageId) return;
        if (type === 'delete') {
          setMessages((prev) => prev.map((item) => (
            item._id === messageId
              ? {
                ...item,
                ...(data?.isDeleted ? { isDeleted: data.isDeleted } : { isDeleted: { ...(item.isDeleted || {}), forAll: true } }),
                content: 'Message Deleted',
              }
              : item
          )));
          return;
        }
        if (type === 'edit') {
          setMessages((prev) => prev.map((item) => (
            item._id === messageId
              ? { ...item, ...(data || {}), content: data?.content ?? item.content, isEdited: true }
              : item
          )));
        }
      });
      return () => socket.disconnect();
    }
  }, []);

  useEffect(() => {
    if (!isConnected || !threadId) return;
    _socket.current?.emit('fetch-previous-chats-update-status', { threadId }, ({ success, chats }) => {
      if (success && Array.isArray(chats)) {
        setMessages(chats);
        scrollToBottom();
      }
    });
  }, [isConnected, threadId]);

  useEffect(() => {
    fetchQueryDetails();
  }, [id]);

  const getStatusColor = (s) => {
    if (s === 'pending') return 'warning';
    if (s === 'solved') return 'success';
    if (s === 'escalated') return 'error';
    return 'default';
  };
  const getStatusText = (s) => {
    if (s === 'pending') return 'Pending';
    if (s === 'solved') return 'Solved';
    if (s === 'escalated') return 'Escalated';
    return 'Open';
  };

  if (loading) {
    return (
      <div className="support-loading">
        <div className="loading-dot-pulse">
          <span /><span /><span />
        </div>
        <p>Loading ticket…</p>
      </div>
    );
  }

  return (
    <div className="support-page-root">
      <div className="support-container">
          <div className="support-card">

            {/* ── Main Header ── */}
            <div className="support-header">
              <div className="header-left">
                <Link to={-1} style={{ textDecoration: 'none' }}>
                  <IconButton size="small" className="hdr-back-btn">
                    <ArrowBackIosNewIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </Link>
                <div className="hdr-avatar">{initials}</div>
                <div className="hdr-user-block">
                  <div className="hdr-name-row">
                    <span className="hdr-name">{userName}</span>
                    <Chip
                      label={getStatusText(queryDetails?.status || 'open')}
                      color={getStatusColor(queryDetails?.status || 'open')}
                      size="small"
                      className="hdr-status-chip"
                    />
                    {queryDetails?.supportType && (
                      <span className="hdr-category-tag">{queryDetails.supportType}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="hdr-actions">
                {isMobile && (
                  <IconButton size="small" className="hdr-back-btn" onClick={() => setDrawerOpen(true)}>
                    <MenuIcon style={{ fontSize: 20 }} />
                  </IconButton>
                )}
                <button onClick={() => handleStatusAction('completed')} className="hdr-btn hdr-btn-solved">
                  ✓ <span>Mark Solved</span>
                </button>
                <button onClick={() => handleStatusAction('escalated')} className="hdr-btn hdr-btn-escalate">
                  ↑ <span>Escalate</span>
                </button>
              </div>
            </div>

            {/* ── User Details Sub-header ── */}
            <div className="support-subheader">
              <div className="subhdr-row">
                <div className="subhdr-card">
                  <div className="subhdr-icon-wrap subhdr-icon-blue">
                    <CategoryIcon style={{ fontSize: 18, color: '#2563eb' }} />
                  </div>
                  <div className="subhdr-info">
                    <div className="subhdr-value">{userName}</div>
                    <div className="subhdr-label">USER NAME</div>
                  </div>
                </div>

                <div className="subhdr-divider" />

                <div className="subhdr-card">
                  <div className="subhdr-icon-wrap subhdr-icon-gray">
                    <PhoneIcon style={{ fontSize: 18, color: '#6b7280' }} />
                  </div>
                  <div className="subhdr-info">
                    <div className="subhdr-value">
                      {phone ? (
                        <a href={`tel:${phonePrefix}${phone}`} className="subhdr-link">
                          {phonePrefix}{phone}
                        </a>
                      ) : 'N/A'}
                    </div>
                    <div className="subhdr-label">CONTACT NO.</div>
                  </div>
                </div>

                <div className="subhdr-card">
                  <div className="subhdr-icon-wrap subhdr-icon-gray">
                    <EmailIcon style={{ fontSize: 18, color: '#6b7280' }} />
                  </div>
                  <div className="subhdr-info">
                    <div className="subhdr-value">{email || 'N/A'}</div>
                    <div className="subhdr-label">EMAIL ID</div>
                  </div>
                </div>

                <div className="subhdr-divider" />

                <div className="subhdr-card">
                  <div className="subhdr-icon-wrap subhdr-icon-gray">
                    <CategoryIcon style={{ fontSize: 18, color: '#6b7280' }} />
                  </div>
                  <div className="subhdr-info">
                    <div className="subhdr-value">{queryDetails?.supportType || 'N/A'}</div>
                    <div className="subhdr-label">PROBLEM TYPE</div>
                  </div>
                </div>
              </div>

              <div className="subhdr-row subhdr-row-problem">
                <div className="subhdr-card subhdr-card-title">
                  <div className="subhdr-icon-wrap subhdr-icon-gray">
                    <DescriptionIcon style={{ fontSize: 18, color: '#6b7280' }} />
                  </div>
                  <div className="subhdr-info">
                    <div className="subhdr-value subhdr-wrap-text">{queryDetails?.title || 'N/A'}</div>
                    <div className="subhdr-label">PROBLEM TITLE</div>
                  </div>
                </div>

                <div className="subhdr-divider" />

                <div className="subhdr-card subhdr-card-desc">
                  <div className="subhdr-icon-wrap subhdr-icon-gray">
                    <DescriptionIcon style={{ fontSize: 18, color: '#6b7280' }} />
                  </div>
                  <div className="subhdr-info">
                    <div className="subhdr-value subhdr-desc-text">
                      {queryDetails?.description || 'N/A'}
                    </div>
                    <div className="subhdr-label">PROBLEM DESCRIPTION</div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Body ── */}
            <div className="support-main-content">
              {!isMobile && (
                <DatewisePanel
                  messages={messages}
                  userName={userName}
                  selectedDate={selectedDate}
                  onSelectDate={setSelectedDate}
                />
              )}

              <div className="chat-container">
                <ChatArea
                  messages={messages}
                  messagesEndRef={messagesEndRef}
                  selectedDate={selectedDate}
                  onEdit={handleEditMessage}
                  onDelete={setPendingDelete}
                />

                {(!isTicketResolved || editingMessage) && (
                  <div className="chat-input">
                    <form onSubmit={handleSendMessage}>
                      <div className="chat-input-row">
                        <input
                          type="text"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          placeholder={editingMessage ? 'Edit message…' : 'Type a reply…'}
                        />
                        {editingMessage && (
                          <button type="button" className="chat-cancel-btn" onClick={cancelEdit}>
                            Cancel
                          </button>
                        )}
                        <button type="submit" className="chat-send-btn" disabled={!message.trim() || uploading}>
                          <SendIcon style={{ fontSize: 16 }} />
                          <span>{editingMessage ? 'Update' : 'Send'}</span>
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {isTicketResolved && (
                  <div className="ticket-resolved-bar">
                    This ticket has been resolved.
                  </div>
                )}
              </div>
            </div>

          </div>

          {isMobile && (
            <Drawer
              anchor="right"
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              PaperProps={{ sx: { width: '100%', maxWidth: '360px' } }}
            >
              <MobileDrawerContent
                onClose={() => setDrawerOpen(false)}
                initials={initials}
                userName={userName}
                phone={phone}
                phonePrefix={phonePrefix}
                email={email}
                queryDetails={queryDetails}
              />
            </Drawer>
          )}

          <ModalBox
            status={updatedQueryStatusModal}
            onClose={() => setUpdateQueryStatusModal(false)}
            title={`Update Status to ${getStatusText(statusAction)}`}
            description={`Are you sure you want to mark this query as ${getStatusText(statusAction)}?`}
            action={fetchQueryStatus}
          />
          <ModalBox
            status={Boolean(pendingDelete)}
            onClose={() => setPendingDelete(null)}
            title="Hide this message?"
            description="The message stays in the database and is marked deleted. It will show as deleted on the ticket. It is not removed permanently."
            action={confirmDeleteMessage}
          />
        </div>
    </div>
  );
};

export default ViewSupport;
